<template>
  <!-- PC表示 -->
  <div
    class="side-members__wrapper d-none d-lg-flex">
    <div
      ref="sideMembers"
      style="overflow: scroll;cursor: pointer;"
      class="side-members">
      <!-- ログインしているユーザー情報 -->
      <sidebar-login-user-tab
        @click="onClickLoginUserTab" />

      <div class="side-members__search-row mr-3">
        <button
          v-if="isManager"
          class="side-members__tag-mode-button ml-3"
          :class="{ 'side-members__tag-mode-button--active': isTagEditMode }"
          @click="isTagEditMode = !isTagEditMode">
          <span class="material-icons side-members__tag-mode-button__icon">local_offer</span>
          <strong>タグ</strong>
        </button>
        <SearchUserForm
          class="side-members__search-form ml-auto"
          outlined
          @submit="searchAndScrollToMember" />
      </div>
      
      <!-- グループ情報 -->
      <ul
        class="side-members__body pb-8 mb-10 list-unstyled">
        <department-tab
          v-for="department in sortedAccessibleDepartments" 
          :ref="department.id"
          :key="department.id"
          :department="department"
          :show-members="isShowedDepartment(department.id)"
          @click.native="onClickDepartmentTab(department.id)">
          <template #pin-icon>
            <div class="side-members__department-actions">
              <button
                v-if="isTagEditMode"
                class="side-members__tag-edit-button"
                :class="{ 'side-members__tag-edit-button--active': activeTagEditDepartmentId === department.id }"
                @click.stop="openTagEditDialog(department)">
                <span class="material-icons side-members__tag-edit-button__icon">local_offer</span>
                <strong>タグの編集</strong>
              </button>
              <button
                v-if="!isTagEditMode && isShowedDepartment(department.id)"
                class="side-members__sort-btn"
                :class="{ 'side-members__sort-btn--active': tagSortOrderByDepartmentId[department.id] != null }"
                @click.stop="toggleTagSort(department.id)">
                <span class="material-icons side-members__sort-icon">
                  {{ tagSortIcon(department.id) }}
                </span>
              </button>
              <div
                class="pin-icon-button"
                @click.stop="togglePin(department.id)">
                <pin-icon
                  white
                  :is-pinned="isPinned(department.id)" />
              </div>
            </div>
          </template>
          <department-member-tab
            v-for="member in sortedMembersByDepartmentId[department.id]"
            :ref="member.id"
            :key="member.id"
            :member="member"
            :selected-date="selectedDate"
            :daily-work-list="dailyWorkListByMemberId[member.id]"
            :is-searched="isSearched(member)"
            @click="onSelectMember(member)">
            <template #suffix>
              <SvgIcon
                class="side-members__item__chart-icon"
                :src="require('@/assets/images/icon_chart.svg')"
                :active="hasSubmittedMandalaEvaluation(member.id)" />
            </template>
          </department-member-tab>
        </department-tab>
      </ul>
      <div
        class="side-members__condition-button">
        <ConditionButton
          xl
          :active="isConditionButtonActive"
          @click="onClickConditionButton" />
        <ProfileButton
          v-if="showProfileButton"
          xl
          :active="isProfileButtonActive"
          @click="onClickProfileButton" />
      </div>
      <ResultListScroller
        v-if="searchedMembers.length"
        :counts="searchedMembers.length"
        @onClickPrev="scrollToPrev"
        @onClickNext="scrollToNext"
        @onClickClose="resetSearchResult" />
    </div>
    <member-tag-edit-dialog
      :show.sync="showTagEditDialog"
      :department="tagEditTargetDepartment"
      :initial-members="tagEditTargetMembers"
      @tag-updated="fetchMembersByDepartmentId" />
  </div>
  <!-- PC表示 end -->
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { sortMembersByTag } from '@/helper/memberSort'
import DepartmentTab from '@/components/view/DepartmentTab.vue'
import MemberTagEditDialog from '@/components/view/MemberTagEditDialog.vue'
import SidebarLoginUserTab from '@/components/view/SidebarLoginUserTab'
import DepartmentMemberTab from '../DepartmentMemberTab.vue'
import SvgIcon from '@/components/parts/SvgIcon.vue'
import ConditionButton from '@/components/parts/ConditionButton.vue'
import ProfileButton from '@/components/parts/ProfileButton.vue'
import { groupBy, uniq } from 'lodash'
import { addMonths, endOfMonth, format, startOfMonth } from 'date-fns'
import PinIcon from '@/components/parts/PinIcon.vue'
import SearchUserForm from '../SearchUserForm.vue'
import ResultListScroller from '@/components/parts/ResultListScroller.vue'

export default {
  components: {
    DepartmentTab,
    SidebarLoginUserTab,
    DepartmentMemberTab,
    SvgIcon,
    PinIcon,
    SearchUserForm,
    ResultListScroller,
    ConditionButton,
    ProfileButton,
    MemberTagEditDialog
  },
  data() {
    return {
      profile: null,
      mandalaEvaluationStatuses: [],
      evaluationsStatusList: [],
      mandalaEvaluationStatusesByDepartmentId: {},
      dailyWorkListByDepartmentId: {},
      showedDepartmentIds: [],
      membersByDepartmentId: {},
      searchedMembers: [],
      currentSearchedMembersIndex: 0,
      isTagEditMode: false,
      tagEditTargetDepartment: null,
      tagEditTargetMembers: null,
      showTagEditDialog: false,
      activeTagEditDepartmentId: null,
      tagSortOrderByDepartmentId: {},
    }
  },
  computed: {
    ...mapState(['selectedDate']),
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['isManager']),
    ...mapState(['isConditionButtonActive']),
    ...mapState('departments', ['pinnedDepartments']),
    ...mapGetters('auth', ['accessibleDepartments']),
    isProfileButtonActive() {
      return this.$route.path.split('/')[3] === 'profile'
    },
    memberId() {
      return this.$route.params.id
    },
    showProfileButton() {
      // 管理者は全てのメンバーのProfileを閲覧できる
      if (this.isManager) {
        return true
      }
      // 保護者はProfileを閲覧できない
      if (this.isParentMember) {
        return false
      }
      // 一般ユーザは本人のProfileを閲覧できる
      return this.memberId === this.user.id
    },
    homeRoute() {
      if (!this.user){
        return '/'
      }
      return `/members/${this.user.id}`
    },
    _pinnedDepartments: {
      get() {
        return this.pinnedDepartments
      },
      set(v) {
        this.$store.commit('departments/setPinnedDepartments', v)
      }
    },
    sortedAccessibleDepartments() {
      const [pinnedDepartments, restDepartments] = this.accessibleDepartments.reduce((prev, curr) => {
        if (this.pinnedDepartments.includes(curr.id)) {
          prev[0].push(curr)
        } else {
          prev[1].push(curr)
        }
        return prev
      }, [[], []])
      return [...pinnedDepartments, ...restDepartments]
    },
    sortedAccessibleDepartmentIds() {
      return this.sortedAccessibleDepartments.map(department => department.id)
    },
    flattenDailyWorkList() {
      return Object.values(this.dailyWorkListByDepartmentId).flat()
    },
    submittedMandalaEvaluationWorks() {
      const flattenMandalaEvaluationStatuses = Object.values(this.mandalaEvaluationStatusesByDepartmentId).flat()
      const submittedMandalaEvaluationStatuses = flattenMandalaEvaluationStatuses.filter(
        evaluationStatus => evaluationStatus.status === 'submitted'
      )
      return submittedMandalaEvaluationStatuses.map(
        evaluation => ({ createdBy: evaluation.user.id, createdDt: evaluation.submittedAt })
      )
    },
    submittedMandalaEvaluationWorksByMemberId() {
      return groupBy(this.submittedMandalaEvaluationWorks, 'createdBy')
    },
    /**
     * @description 今月の自己評価の提出日をdailyWorkListに追加
     */
    dailyWorkListWithSubmittedMandalaEvaluationWorks() {
      return [...this.flattenDailyWorkList, ...this.submittedMandalaEvaluationWorks]
    },
    dailyWorkListByMemberId () {
      return groupBy(this.dailyWorkListWithSubmittedMandalaEvaluationWorks, 'createdBy')
    },
    startDateOfMonth() {
      return startOfMonth(this.selectedDate)
    },
    endDateOfMonth() {
      return endOfMonth(this.selectedDate)
    },
    selectedYYYYMM() {
      return format(this.selectedDate, 'yyyyMM')
    },
    lastYearMonthStr() {
      return format(startOfMonth(addMonths(this.selectedDate, -1)), 'yyyy-MM-dd')
    },
    mandalaEvaluationStatusesByMemberId() {
      return groupBy(this.mandalaEvaluationStatuses, evaluation => evaluation.user.id)
    },
    // $elがあるため、レンダリング終了まで計算が終わらない -> $nextTickでラップする必要がある
    sortedSearchedMembers() {
      return [...this.searchedMembers].sort((a, b) => {
        const aTop = this.$refs[a.id][0].$el.getBoundingClientRect().top
        const bTop = this.$refs[b.id][0].$el.getBoundingClientRect().top
        return aTop <= bTop ? -1 : 1
      })
    },
    searchedMembersDepartmentIds() {
      return uniq(this.searchedMembers.map(member => member.department.id))
    },
    sortedMembersByDepartmentId() {
      const result = {}
      for (const [departmentId, members] of Object.entries(this.membersByDepartmentId)) {
        const sortOrder = this.tagSortOrderByDepartmentId[departmentId]
        if (!sortOrder) {
          result[departmentId] = members
          continue
        }
        result[departmentId] = sortMembersByTag(members, sortOrder)
      }
      return result
    },
  },
  watch: {
    showTagEditDialog(val) {
      if (!val) this.activeTagEditDepartmentId = null
    },
    // 月が変わった時
    async selectedYYYYMM() {
      // メンバー一覧が見えている全てのグループでrefetchする
      await Promise.all(
        this.showedDepartmentIds.map(departmentId => this.refetchByDepartmentId(departmentId))
      )
    }
  },
  async created() {
    await this.$store.dispatch('auth/updateUser')

    // 自分自身の場合はグループを開かずスクロールもしない
    if (this.memberId === this.user.id) {
      return
    }

    // 選択しているメンバーのグループを開く
    const selectedMember = await this.fetchMember(this.memberId)
    const selectedMemberDepartmentId = selectedMember.department.id
    await this.refetchByDepartmentId(selectedMemberDepartmentId)
    this.showedDepartmentIds = [selectedMemberDepartmentId]
    this.$nextTick(() => {
      this.scrollToMember(selectedMember)
    })
  },
  methods: { 
    onClickLoginUserTab() {
      this.$router.push(`/members/${this.user.id}`, () => {})
    },
    isShowedDepartment(departmentId) {
      return this.showedDepartmentIds.includes(departmentId)
    },
    async searchAndScrollToMember(searchKeyword) {
      // メンバーを検索する
      const res = await this.$axios.get('/users/', {
        params: {
          search: searchKeyword,
          ordering: '-sort_no',
          filter_accessible_departments: true
        }
      }).catch(error => {
        console.error(error)
        this.$store.commit('notify/set', {type: 'danger', message: '不明なエラーが発生しました。しばらく時間をあけてアクセスし直してください。なお、連続して発生する場合はログインページ下のアプリ更新を押してください。'})
      })

      this.searchedMembers = res.data.results

      if (this.searchedMembersDepartmentIds.length === 0) {
        this.$store.commit('notify/set', {message: 'メンバーが見つかりませんでした'})
        return
      }

      await Promise.all(
        this.searchedMembersDepartmentIds.map(departmentId => this.refetchByDepartmentId(departmentId))
      )
      // メンバーが所属するグループを全て展開
      this.showedDepartmentIds = Array.from(new Set([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds]))
      
      // 検索されたメンバーのうち、画面上での最初のメンバーの要素にスクロール
      this.$nextTick(() => {
        this.scrollToMember(this.sortedSearchedMembers[0])
      })
    },
    scrollToMember(member) {
      const memberEl = this.$refs[member.id][0].$el
      const targetTop = memberEl.getBoundingClientRect().top + this.$refs.sideMembers.scrollTop
      this.$refs.sideMembers.scroll({
        top: targetTop,
        behavior: 'smooth'
      })
    },
    hasSubmittedMandalaEvaluation(memberId) {
      return this.submittedMandalaEvaluationWorksByMemberId[memberId] !== undefined
    },
    fetchMember(memberId) {
      return this.$axios.get(`/users/${memberId}/`).then(res => {
        return res.data
      })
    },
    fetchMembersByDepartmentId(departmentId) {
      return this.$axios.get('/users/', {
        params: {
          ordering: '-sort_no',
          limit: 99999,
          department__in: departmentId,
        }
      }).then(res => {
        this.$set(this.membersByDepartmentId, departmentId, res.data.results)
      })
    },
    fetchDailyWorkListByDepartmentId(departmentId) {
      return this.$axios.get('/activities/daily_work_list/', {
        params: {
          limit: 9999999,
          created_at__gte: this.startDateOfMonth,
          created_at__lte: this.endDateOfMonth,
          department: departmentId,
        }
      }).then(res => {
        this.$set(this.dailyWorkListByDepartmentId, departmentId, res.data)
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
    },
    fetchMandalaEvaluationStatusesByDepartmentId(departmentId) {
      return this.$axios.post('/mandala_goal_self_evaluations/user_status/', {
        department: departmentId,
        year_month: this.lastYearMonthStr,
      }).then(res => {
        this.$set(this.mandalaEvaluationStatusesByDepartmentId, departmentId, res.data)
      })
    },
    refetchByDepartmentId(departmentId) {
      return Promise.all([
        this.fetchMembersByDepartmentId(departmentId),
        this.fetchDailyWorkListByDepartmentId(departmentId),
        this.fetchMandalaEvaluationStatusesByDepartmentId(departmentId)
      ])
    },
    onClickDepartmentTab(departmentId) {
      if (this.showedDepartmentIds.includes(departmentId)) {
        this.showedDepartmentIds = this.showedDepartmentIds.filter(showedDepartmentId => showedDepartmentId !== departmentId)
      } else {
        this.refetchByDepartmentId(departmentId)
        this.showedDepartmentIds.push(departmentId)
      }
    },
    togglePin(departmentId) {
      if (this.isPinned(departmentId)) {
        this._pinnedDepartments = this._pinnedDepartments.filter(pinnedId => pinnedId !== departmentId)
      } else {
        this._pinnedDepartments = [...this._pinnedDepartments, departmentId]
      }
    },
    isPinned(departmentId) {
      return this.$store.getters['departments/isPinned'](departmentId)
    },
    isSearched(member) {
      return this.searchedMembers.map(member => member.id).includes(member.id)
    },
    scrollToPrev() {
      this.showedDepartmentIds = uniq([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds])
      this.currentSearchedMembersIndex = this.currentSearchedMembersIndex === 0 ? this.searchedMembers.length - 1 : this.currentSearchedMembersIndex - 1
      this.$nextTick(() => {
        this.scrollToMember(this.sortedSearchedMembers[this.currentSearchedMembersIndex])
      })
    },
    scrollToNext() {
      this.showedDepartmentIds = uniq([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds])
      this.currentSearchedMembersIndex = (this.currentSearchedMembersIndex + 1) % this.searchedMembers.length
      this.$nextTick(() => {
        this.scrollToMember(this.sortedSearchedMembers[this.currentSearchedMembersIndex])
      })
    },
    resetSearchResult() {
      this.searchedMembers = []
    },
    onClickConditionButton() {
      this.$store.commit('toggleIsConditionButtonActive')
    },
    onClickProfileButton() {
      if (this.isProfileButtonActive) {
        this.$router.push(`/members/${this.memberId}`)
      } else {
        this.$router.push(`/members/${this.memberId}/profile`)
      }
    },
    onSelectMember(member) {
      this.$emit('select-member', member)
    },
    toggleTagSort(departmentId) {
      const members = this.membersByDepartmentId[departmentId] ?? []
      const hasTaggedMember = members.some((m) => m.tag1 || m.tag2)
      if (!hasTaggedMember) {
        this.$store.commit('notify/set', {
          type: 'info',
          message:
            'このグループにタグのついた選手はいません。タグ付けを行うとタグで並べ替えることができます',
        })
        return
      }
      const current = this.tagSortOrderByDepartmentId[departmentId] ?? null
      const next = current === null ? 'asc' : current === 'asc' ? 'desc' : null
      this.$set(this.tagSortOrderByDepartmentId, departmentId, next)
    },
    tagSortIcon(departmentId) {
      const order = this.tagSortOrderByDepartmentId[departmentId]
      if (order === 'asc') return 'arrow_upward'
      if (order === 'desc') return 'arrow_downward'
      return 'swap_vert'
    },
    async openTagEditDialog(department) {
      this.tagEditTargetDepartment = department
      this.activeTagEditDepartmentId = department.id
      if (!this.membersByDepartmentId[department.id]) {
        try {
          await this.fetchMembersByDepartmentId(department.id)
        } catch {
          this.$store.commit('notify/set', { isUnknownError: true, type: 'danger' })
          this.activeTagEditDepartmentId = null
          return
        }
      }
      this.tagEditTargetMembers = this.sortedMembersByDepartmentId[department.id]
      this.showTagEditDialog = true
    },
  }
}
</script>

<style lang="scss" scoped>
.side-members__wrapper {
  width: 302px;
  min-width: 302px;
}

.side-members{
  background-color: $white;
  width: 302px;
  height: 100vh;
  position: fixed;
  top: 0;

  &__head{
    display: block;
    color: $black;
    &:hover{
      color: $white;
      cursor: pointer;
    }
  }
  &__content{
    background-color: $white;
    border-radius: 8px;
    border-bottom: solid 1px $white;
  }
  &__list{
    border-top: solid 1px $grey03;
  }
  &__item{
    position: relative;

    &__chart-icon {
      color: $grey03;
    }
  }
  &__btn{
    position: relative;
    background-color: $l-blue;
    box-shadow: 1px 2px 6px $grey05;
  }
  &__btn-img{
    width: 60px;
    height: auto;
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    top: 40%;
  }
  &__search-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__search-form {
    ::v-deep .members__search {
      max-width: 140px;
      width: 100%;
      margin: 0;
    }
  }

  &__tag-mode-button {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    background: $white;
    border: none;
    border-radius: 4px;
    color: $l-blue;
    font-size: 12px;
    padding: 4px 10px;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 1px 2px 6px 0 $grey05;

    &--active {
      background: $l-blue;
      color: $white;
    }

    &__icon {
      font-size: 14px;
    }
  }

  &__department-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__tag-edit-button {
    display: flex;
    align-items: center;
    gap: 4px;
    background: $white;
    border: none;
    border-radius: 4px;
    color: $l-blue;
    font-size: 12px;
    padding: 4px 10px;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 1px 2px 6px 0 $grey05;

    &--active {
      background: $l-blue;
      color: $white;
    }

    &__icon {
      font-size: 14px;
    }
  }

  &__sort-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;

    &--active {
      color: $white;
    }
  }

  &__sort-icon {
    font-size: 24px;
  }

  &__condition-button {
    position: fixed;
    bottom: 24px;
    left: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }
}

.result-list-scroller {
  bottom: 25px;
  left: 330px;
}
</style>